import { Box, Divider, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CloudDownload } from '@material-ui/icons';
import { Document } from '@roc/feature-types';
import { CustomTaskDialog } from '@roc/feature-general-tasks';
import { Button, StandardDialog } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { DocumentSigningStore } from '../../../stores/documentForms/documentSigningStore';
import { TrackingNumber } from '../trackingNumber/trackingNumber';
import { isAccepted } from '../../../utils/documents';
import {
  WET_SIGN_DOCUMENT_TITLES,
  WET_SIGN_DOCUMENT_TYPES,
} from './wetSignConstants';

const useStyles = makeStyles(theme => ({
  step: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  address: {
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1),
    maxWidth: 440,
  },
}));

interface Props {
  onClose: () => void;
  open: boolean;
  document: Document;
  loan: any;
  store: DocumentSigningStore;
}

const WetSignDocumentDialog = observer((props: Props) => {
  const { open, onClose, document, loan, store } = props;
  const classes = useStyles();
  const taskId = document?.taskId;
  const documentType = WET_SIGN_DOCUMENT_TYPES[document?.taskName];
  const documentTitle = WET_SIGN_DOCUMENT_TITLES[documentType];
  const title = `${documentTitle} - WS`;
  // once accepted the task stays openable to see the saved tracking number, which can no longer change
  const readOnly = isAccepted(document);

  const handleDownload = () => {
    store.downloadWetSignDocument(loan?.loanId, documentType);
  };

  // the FedEx tracking number is kept in the task description, and saving it completes the task
  useEffect(() => {
    if (open) {
      store.setWetSignTrackingNumber(document?.description ?? '');
      store.fetchWetSignMailingAddress();
    }
  }, [open, taskId]);

  const handleSave = async () => {
    const saved = await store.saveWetSignTrackingNumber(taskId);
    if (saved) {
      onClose();
    }
  };

  const dialogContent = (
    <Grid container>
      <Grid item xs={12}>
        <Typography className={classes.step}>
          1. Download and print the {documentTitle}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Print the document, wet sign it, and mail the original via FedEx
          {store.wetSignMailingAddress.length > 0 ? ' to:' : '.'}
        </Typography>
        {store.wetSignMailingAddress.length > 0 && (
          <Box mb={1} className={classes.address}>
            {store.wetSignMailingAddress.map(line => (
              <Typography key={line} variant="body2">
                {line}
              </Typography>
            ))}
          </Box>
        )}
        <Button
          testId="downloadWetSignDocument"
          variant="outlined"
          color="primary"
          startIcon={<CloudDownload />}
          onClick={handleDownload}
        >
          Download {documentTitle}
        </Button>
      </Grid>

      <Grid item xs={12}>
        <Divider className={classes.divider} />
        <Typography className={classes.step}>
          2. Enter the FedEx tracking number
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          If you shipped the Note Allonge and the Assignment and Conveyance in the
          same package, enter the same tracking number in both tasks. <br /> Your TPO
          points are paid once we confirm the originals were received.
        </Typography>
        <Box maxWidth={320} mt={2}>
          <TrackingNumber
            value={store.wetSignTrackingNumber}
            onChange={value => store.setWetSignTrackingNumber(value)}
            disabled={readOnly}
          />
        </Box>
      </Grid>
    </Grid>
  );

  const dialogActions = (
    <Grid container spacing={1} justifyContent="flex-end">
      <Grid item>
        <Button testId="cancel" variant="outlined" color="primary" onClick={onClose}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
      </Grid>
      {!readOnly && <Grid item>
        <Button
          testId="saveWetSignTrackingNumber"
          variant="contained"
          color="primary"
          disabled={!store.wetSignTrackingNumber?.trim()}
          onClick={handleSave}
        >
          Save
        </Button>
      </Grid>}
    </Grid>
  );

  return taskId ? (
    <CustomTaskDialog
      title={title}
      open={open}
      maxWidth={'md'}
      handleClose={onClose}
      dialogContent={dialogContent}
      dialogActions={dialogActions}
      taskId={taskId}
    />
  ) : (
    <StandardDialog
      title={title}
      open={open}
      maxWidth={'md'}
      handleClose={onClose}
      dialogContent={dialogContent}
      dialogActions={dialogActions}
    />
  );
});

export default WetSignDocumentDialog;
